import React, { useState } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import Layout from '../components/Layout'

const NAV = [
  { path: '/admin',            icon: '🎯', label: 'Overview' },
  { path: '/admin/clients',    icon: '👤', label: 'All Clients' },
  { path: '/admin/pipeline',   icon: '💰', label: 'Pipeline' },
  { path: '/admin/analytics',  icon: '📊', label: 'Analytics' },
  { path: '/admin/billing',    icon: '💳', label: 'Billing' },
  { path: '/admin/messages',   icon: '💬', label: 'Messages' },
  { path: '/admin/tasks',      icon: '✅', label: 'Tasks' },
  { path: '/admin/marketing',  icon: '📣', label: 'Marketing' },
  { path: '/admin/calendar',   icon: '🗓️', label: 'Calendar' },
  { path: '/admin/content',    icon: '📅', label: 'Content Mgr' },
  { path: '/admin/invite',     icon: '✉️',  label: 'Invite Client' },
]

export default function AdminDashboard({ profile }) {
  return (
    <Layout profile={profile} navItems={NAV} title="Admin">
      <Routes>
        <Route path="/"           element={<AdminOverview />} />
        <Route path="/clients"    element={<AdminClients />} />
        <Route path="/pipeline"   element={<AdminPipeline />} />
        <Route path="/analytics"  element={<AdminAnalytics />} />
        <Route path="/billing"    element={<AdminBilling />} />
        <Route path="/messages"   element={<AdminMessages />} />
        <Route path="/tasks"      element={<AdminTasks />} />
        <Route path="/marketing"  element={<AdminMarketing />} />
        <Route path="/calendar"   element={<AdminCalendar />} />
        <Route path="/content"    element={<AdminContent />} />
        <Route path="/invite"     element={<AdminInvite />} />
      </Routes>
    </Layout>
  )
}

/* ── SHARED ─────────────────────────────────────────────────────────────── */
function SectionLabel({ children }) {
  return (
    <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 12, fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ width: 24, height: 2, background: 'var(--gold)', display: 'inline-block' }} />
      {children}
    </div>
  )
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="fade-up" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderTop: `3px solid ${accent || 'var(--gold)'}`, padding: '24px 24px 20px' }}>
      <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 44, color: accent || 'var(--gold)', lineHeight: 1, marginBottom: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 13, color: 'var(--muted)' }}>{sub}</div>}
    </div>
  )
}

function Card({ children, style }) {
  return <div style={{ background: 'var(--card)', border: '1px solid var(--border)', padding: '20px 22px', ...style }}>{children}</div>
}

function Badge({ children, color }) {
  const c = color || 'var(--gold)'
  return (
    <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', padding: '3px 10px', background: c + '22', color: c, border: `1px solid ${c}44`, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  )
}

/* ── MOCK DATA ──────────────────────────────────────────────────────────── */
const MOCK_CLIENTS = [
  { id: 1, name: 'Marcus Thompson', team: 'G-League — Motor City', package: 'Build', revenue: 3000, status: 'Active', joined: '2026-04-01', email: 'marcus@email.com', nextAction: 'Monthly call — June 1st', health: 'green' },
  { id: 2, name: 'Destiny Williams', team: 'WNBA — Atlanta Dream', package: 'Operate', revenue: 7500, status: 'Active', joined: '2026-03-15', email: 'destiny@email.com', nextAction: 'Weekly call — Thursday', health: 'green' },
  { id: 3, name: 'Jordan Rivera', team: 'Overseas — Spain ACB', package: 'Launch', revenue: 1500, status: 'Active', joined: '2026-05-10', email: 'jordan@email.com', nextAction: 'Deliver website by June 3', health: 'yellow' },
  { id: 4, name: 'Caleb Foster', team: 'NIL — Rutgers', package: 'Build', revenue: 3000, status: 'Active', joined: '2026-05-01', email: 'caleb@email.com', nextAction: 'Send content calendar', health: 'green' },
  { id: 5, name: 'Amir Johnson', team: 'G-League — Westchester', package: 'Build', revenue: 3000, status: 'Onboarding', joined: '2026-05-22', email: 'amir@email.com', nextAction: 'Collect onboarding form', health: 'yellow' },
]

const PIPELINE = [
  { name: 'Shanice Davis', market: 'WNBA', package: 'Operate $7,500', status: 'Call Booked', date: '2026-06-02', value: 7500 },
  { name: 'DeShawn Morris', market: 'NBA Young Gun', package: 'Build $3,000', status: 'Responded', date: '2026-05-28', value: 3000 },
  { name: 'Tyler Okonkwo', market: 'Overseas', package: 'Build $3,000', status: 'Contacted', date: '2026-05-25', value: 3000 },
  { name: 'Brandon Wells', market: 'G-League', package: 'Launch $1,500', status: 'Contacted', date: '2026-05-24', value: 1500 },
  { name: 'Marcus Lee', market: 'NFL', package: 'Operate $7,500', status: 'Call Booked', date: '2026-06-05', value: 7500 },
  { name: 'Isaiah Grant', market: 'NIL', package: 'Build $3,000', status: 'Responded', date: '2026-05-27', value: 3000 },
]

/* ── OVERVIEW ───────────────────────────────────────────────────────────── */
function AdminOverview() {
  const navigate = useNavigate()
  const mrr = MOCK_CLIENTS.reduce((sum, c) => sum + (c.status === 'Active' ? c.revenue : 0), 0)
  const pipeline = PIPELINE.reduce((sum, p) => sum + p.value, 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <div className="fade-up" style={{ background: 'var(--gold)', padding: '28px 32px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: -10, top: '50%', transform: 'translateY(-50%)', fontFamily: 'var(--font-display)', fontSize: 180, color: 'rgba(0,0,0,0.06)', pointerEvents: 'none' }}>ADMIN</div>
        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase', color: 'rgba(0,0,0,0.45)', marginBottom: 8 }}>The Player Office</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 48, color: 'var(--black)', lineHeight: 0.95, marginBottom: 8 }}>Admin<br />Dashboard</div>
        <div style={{ fontSize: 15, color: 'rgba(0,0,0,0.55)' }}>Full visibility into every client, deal, and dollar.</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
        <StatCard label="Monthly Revenue" value={`$${mrr.toLocaleString()}`} sub="Active clients only" accent="var(--green)" />
        <StatCard label="Active Clients" value={MOCK_CLIENTS.filter(c => c.status === 'Active').length} sub="Paying retainers" accent="var(--gold)" />
        <StatCard label="Pipeline Value" value={`$${pipeline.toLocaleString()}`} sub="Prospects in play" accent="#9C27B0" />
        <StatCard label="Annual Run Rate" value={`$${(mrr * 12).toLocaleString()}`} sub="Based on current MRR" accent="#1565C0" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        {[
          { label: 'Analytics', icon: '📊', path: '/admin/analytics', desc: 'Revenue & growth charts' },
          { label: 'Billing', icon: '💳', path: '/admin/billing', desc: 'Stripe payment tracking' },
          { label: 'Messages', icon: '💬', path: '/admin/messages', desc: 'Client communications' },
          { label: 'Tasks', icon: '✅', path: '/admin/tasks', desc: 'Per-client to-do lists' },
          { label: 'Marketing', icon: '📣', path: '/admin/marketing', desc: 'Outreach & campaigns' },
          { label: 'Calendar', icon: '🗓️', path: '/admin/calendar', desc: 'Schedule & appointments' },
        ].map(item => (
          <button key={item.path} onClick={() => navigate(item.path)}
            style={{ background: 'var(--card)', border: '1px solid var(--border)', padding: '18px 16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.background = 'var(--card2)' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--card)' }}>
            <span style={{ fontSize: 24 }}>{item.icon}</span>
            <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 13, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--white)' }}>{item.label}</span>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>{item.desc}</span>
          </button>
        ))}
      </div>

      <SectionLabel>Client Health</SectionLabel>
      <Card style={{ padding: 0 }}>
        {MOCK_CLIENTS.map((c, i) => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', borderBottom: i < MOCK_CLIENTS.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: c.health === 'green' ? 'var(--green)' : 'var(--gold)', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)' }}>{c.name}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{c.team}</div>
            </div>
            <Badge color={c.package === 'Operate' ? 'var(--green)' : 'var(--gold)'}>{c.package}</Badge>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--white)', minWidth: 80, textAlign: 'right' }}>${c.revenue.toLocaleString()}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', flex: 1, minWidth: 180, textAlign: 'right' }}>{c.nextAction}</div>
          </div>
        ))}
      </Card>
    </div>
  )
}

/* ── ALL CLIENTS ────────────────────────────────────────────────────────── */
function AdminClients() {
  const navigate = useNavigate()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <SectionLabel>All Clients ({MOCK_CLIENTS.length})</SectionLabel>
        <button onClick={() => navigate('/admin/invite')}
          style={{ background: 'var(--gold)', border: 'none', color: 'var(--black)', fontFamily: 'var(--font-condensed)', fontSize: 12, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', padding: '10px 20px', cursor: 'pointer' }}>
          + Invite Client
        </button>
      </div>
      {MOCK_CLIENTS.map(c => (
        <Card key={c.id}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 20, fontWeight: 800, letterSpacing: 1, color: 'var(--white)', textTransform: 'uppercase', marginBottom: 4 }}>{c.name}</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>{c.team} · Joined {c.joined}</div>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Badge color={c.status === 'Active' ? 'var(--green)' : 'var(--gold)'}>{c.status}</Badge>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, color: 'var(--gold)' }}>${c.revenue.toLocaleString()}/mo</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {[['Package', c.package], ['Email', c.email], ['Next Action', c.nextAction]].map(([label, val]) => (
              <div key={label} style={{ background: 'var(--dark)', padding: '12px 16px' }}>
                <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 13, color: label === 'Next Action' ? 'var(--gold)' : 'var(--light)' }}>{val}</div>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  )
}

/* ── PIPELINE ────────────────────────────────────────────────────────────── */
function AdminPipeline() {
  const statusColor = { 'Call Booked': 'var(--green)', 'Responded': '#1565C0', 'Contacted': 'var(--gold)', 'Proposal Sent': '#9C27B0' }
  const total = PIPELINE.reduce((s, p) => s + p.value, 0)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <StatCard label="Pipeline Value" value={`$${total.toLocaleString()}`} accent="var(--gold)" />
        <StatCard label="Prospects" value={PIPELINE.length} sub="In play" accent="var(--green)" />
        <StatCard label="Calls Booked" value={PIPELINE.filter(p => p.status === 'Call Booked').length} accent="#1565C0" />
      </div>
      <SectionLabel>Active Prospects</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {PIPELINE.map((p, i) => (
          <Card key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              <div>
                <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 16, fontWeight: 800, color: 'var(--white)', textTransform: 'uppercase', marginBottom: 4 }}>{p.name}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Badge color="var(--muted)">{p.market}</Badge>
                  <Badge color={statusColor[p.status] || 'var(--gold)'}>{p.status}</Badge>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, color: 'var(--gold)' }}>${p.value.toLocaleString()}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{p.package}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}

/* ── ANALYTICS ──────────────────────────────────────────────────────────── */
function AdminAnalytics() {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']
  const revenue = [0, 3000, 3000, 7500, 15000, 18000]
  const clients = [0, 1, 1, 2, 4, 5]
  const maxRev = Math.max(...revenue)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <StatCard label="MRR Growth" value="+140%" sub="Last 90 days" accent="var(--green)" />
        <StatCard label="Avg Client Value" value="$4,200" sub="Per month" accent="var(--gold)" />
        <StatCard label="Churn Rate" value="0%" sub="Last 60 days" accent="var(--green)" />
        <StatCard label="LTV Estimate" value="$25K" sub="Per client avg" accent="#9C27B0" />
      </div>

      <SectionLabel>Monthly Revenue Growth</SectionLabel>
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 180, padding: '0 8px' }}>
          {months.map((month, i) => (
            <div key={month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--gold)', fontWeight: 700 }}>{revenue[i] > 0 ? `$${(revenue[i]/1000).toFixed(0)}K` : ''}</div>
              <div style={{ width: '100%', background: i === months.length - 1 ? 'var(--gold)' : 'rgba(212,169,64,0.3)', height: maxRev > 0 ? `${(revenue[i] / maxRev) * 140}px` : '4px', minHeight: 4, transition: 'height 0.5s ease' }} />
              <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700, letterSpacing: 2, color: 'var(--muted)', textTransform: 'uppercase' }}>{month}</div>
            </div>
          ))}
        </div>
      </Card>

      <SectionLabel>Client Growth</SectionLabel>
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: 140, padding: '0 8px' }}>
          {months.map((month, i) => (
            <div key={month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--green)', fontWeight: 700 }}>{clients[i] > 0 ? clients[i] : ''}</div>
              <div style={{ width: '100%', background: i === months.length - 1 ? 'var(--green)' : 'rgba(76,175,80,0.3)', height: `${(clients[i] / 5) * 100}px`, minHeight: 4 }} />
              <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700, letterSpacing: 2, color: 'var(--muted)', textTransform: 'uppercase' }}>{month}</div>
            </div>
          ))}
        </div>
      </Card>

      <SectionLabel>Revenue by Package</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        {[
          { pkg: 'Operate', rev: 7500, clients: 1, color: 'var(--green)' },
          { pkg: 'Build', rev: 9000, clients: 3, color: 'var(--gold)' },
          { pkg: 'Launch', rev: 1500, clients: 1, color: '#1565C0' },
        ].map(p => (
          <Card key={p.pkg}>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 13, fontWeight: 800, letterSpacing: 2, color: p.color, textTransform: 'uppercase', marginBottom: 8 }}>{p.pkg}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, color: 'var(--white)', marginBottom: 4 }}>${p.rev.toLocaleString()}/mo</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{p.clients} client{p.clients > 1 ? 's' : ''}</div>
          </Card>
        ))}
      </div>
    </div>
  )
}

/* ── BILLING ────────────────────────────────────────────────────────────── */
function AdminBilling() {
  const invoices = [
    { client: 'Destiny Williams', amount: 7500, status: 'Paid', date: '2026-06-01', package: 'Operate', method: 'Stripe' },
    { client: 'Marcus Thompson', amount: 3000, status: 'Paid', date: '2026-06-01', package: 'Build', method: 'Stripe' },
    { client: 'Caleb Foster', amount: 3000, status: 'Paid', date: '2026-06-01', package: 'Build', method: 'Stripe' },
    { client: 'Amir Johnson', amount: 3000, status: 'Pending', date: '2026-06-05', package: 'Build', method: 'Invoice' },
    { client: 'Jordan Rivera', amount: 1500, status: 'Paid', date: '2026-05-10', package: 'Launch', method: 'Stripe' },
    { client: 'Destiny Williams', amount: 7500, status: 'Paid', date: '2026-05-01', package: 'Operate', method: 'Stripe' },
    { client: 'Marcus Thompson', amount: 3000, status: 'Paid', date: '2026-05-01', package: 'Build', method: 'Stripe' },
  ]

  const totalPaid = invoices.filter(i => i.status === 'Paid').reduce((s, i) => s + i.amount, 0)
  const pending = invoices.filter(i => i.status === 'Pending').reduce((s, i) => s + i.amount, 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <StatCard label="Total Collected" value={`$${totalPaid.toLocaleString()}`} accent="var(--green)" />
        <StatCard label="Pending" value={`$${pending.toLocaleString()}`} accent="var(--gold)" />
        <StatCard label="Invoices" value={invoices.length} sub="All time" accent="var(--gold)" />
      </div>

      <SectionLabel>Payment History</SectionLabel>
      <Card style={{ padding: 0 }}>
        {invoices.map((inv, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: i < invoices.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)', marginBottom: 2 }}>{inv.client}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{inv.package} · {inv.method} · {inv.date}</div>
            </div>
            <Badge color={inv.status === 'Paid' ? 'var(--green)' : 'var(--gold)'}>{inv.status}</Badge>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: inv.status === 'Paid' ? 'var(--white)' : 'var(--gold)', minWidth: 80, textAlign: 'right' }}>${inv.amount.toLocaleString()}</div>
          </div>
        ))}
      </Card>

      <Card style={{ borderColor: 'var(--gold)', background: 'rgba(212,169,64,0.05)' }}>
        <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 8 }}>Connect Stripe</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>To automate billing and track real-time payments, connect your Stripe account. Visit stripe.com to set up recurring subscriptions for each client package.</div>
      </Card>
    </div>
  )
}

/* ── MESSAGES ────────────────────────────────────────────────────────────── */
function AdminMessages() {
  const [active, setActive] = useState(MOCK_CLIENTS[0].name)
  const [input, setInput] = useState('')

  const threads = {
    'Marcus Thompson': [
      { from: 'Marcus Thompson', text: 'Hey just checking on the content calendar for this week', time: '2:30 PM' },
      { from: 'You', text: 'Sending it over tonight — 8 posts scheduled for the week', time: '2:45 PM' },
      { from: 'Marcus Thompson', text: 'Perfect, thanks!', time: '2:46 PM' },
    ],
    'Destiny Williams': [
      { from: 'Destiny Williams', text: 'The Nike deal — any update?', time: '10:15 AM' },
      { from: 'You', text: "Following up today. They're reviewing the proposal.", time: '10:30 AM' },
    ],
    'Jordan Rivera': [
      { from: 'Jordan Rivera', text: 'When will the website be ready?', time: 'Yesterday' },
      { from: 'You', text: 'June 3rd — I\'ll send you a preview link by tomorrow', time: 'Yesterday' },
    ],
    'Caleb Foster': [
      { from: 'Caleb Foster', text: 'Got the NIL deal from Under Armour — need help reviewing', time: '9:00 AM' },
      { from: 'You', text: 'Send it over and I\'ll look at it today', time: '9:15 AM' },
    ],
    'Amir Johnson': [
      { from: 'You', text: 'Welcome to The Player Office! Please fill out the onboarding form', time: 'May 22' },
      { from: 'Amir Johnson', text: 'Will do, thanks for everything', time: 'May 22' },
    ],
  }

  const msgs = threads[active] || []

  return (
    <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 140px)', minHeight: 500 }}>
      <div style={{ width: 220, flexShrink: 0, background: 'var(--dark)', border: '1px solid var(--border)', borderRight: 'none', overflowY: 'auto' }}>
        {MOCK_CLIENTS.map(c => (
          <button key={c.id} onClick={() => setActive(c.name)}
            style={{ width: '100%', padding: '14px 16px', textAlign: 'left', background: active === c.name ? 'var(--card)' : 'transparent', border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer', borderLeft: active === c.name ? '3px solid var(--gold)' : '3px solid transparent' }}>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 12, fontWeight: 700, letterSpacing: 1, color: active === c.name ? 'var(--gold)' : 'var(--white)', textTransform: 'uppercase', marginBottom: 3 }}>{c.name.split(' ')[0]}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.package}</div>
          </button>
        ))}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-condensed)', fontSize: 14, fontWeight: 800, letterSpacing: 2, color: 'var(--white)', textTransform: 'uppercase' }}>{active}</div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {msgs.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.from === 'You' ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '75%', padding: '10px 14px', background: m.from === 'You' ? 'var(--gold)' : 'var(--dark)', color: m.from === 'You' ? 'var(--black)' : 'var(--light)', fontSize: 13, lineHeight: 1.6, border: m.from === 'You' ? 'none' : '1px solid var(--border)' }}>
                <div>{m.text}</div>
                <div style={{ fontSize: 10, color: m.from === 'You' ? 'rgba(0,0,0,0.4)' : 'var(--muted)', marginTop: 4 }}>{m.time}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', borderTop: '1px solid var(--border)' }}>
          <input value={input} onChange={e => setInput(e.target.value)} placeholder="Type a message..."
            style={{ flex: 1, padding: '12px 16px', background: 'var(--dark)', border: 'none', fontSize: 14, color: 'var(--white)' }} />
          <button onClick={() => setInput('')}
            style={{ background: 'var(--gold)', border: 'none', color: 'var(--black)', fontFamily: 'var(--font-condensed)', fontSize: 12, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', padding: '12px 20px', cursor: 'pointer' }}>
            Send
          </button>
        </div>
      </div>
    </div>
  )
}

/* ── TASKS ──────────────────────────────────────────────────────────────── */
function AdminTasks() {
  const [tasks, setTasks] = useState([
    { id: 1, client: 'Marcus Thompson', task: 'Send weekly content calendar', due: 'Today', priority: 'High', done: false },
    { id: 2, client: 'Destiny Williams', task: 'Follow up with Nike on sponsorship proposal', due: 'Today', priority: 'High', done: false },
    { id: 3, client: 'Jordan Rivera', task: 'Complete website build and send preview link', due: 'Jun 3', priority: 'High', done: false },
    { id: 4, client: 'Caleb Foster', task: 'Review NIL contract from Under Armour', due: 'Jun 2', priority: 'High', done: false },
    { id: 5, client: 'Amir Johnson', task: 'Chase onboarding form submission', due: 'Jun 1', priority: 'Medium', done: false },
    { id: 6, client: 'All Clients', task: 'Send June monthly reports', due: 'Jul 1', priority: 'Medium', done: false },
    { id: 7, client: 'Marketing', task: 'Post 4 pieces of content this week', due: 'Jun 7', priority: 'Medium', done: false },
    { id: 8, client: 'Pipeline', task: 'Follow up with Shanice Davis after discovery call', due: 'Jun 2', priority: 'High', done: false },
    { id: 9, client: 'Marcus Thompson', task: 'Schedule quarterly review call', due: 'Jun 10', priority: 'Low', done: true },
  ])

  const toggle = id => setTasks(prev => prev.map(t => t.id === id ? { ...t, done: !t.done } : t))
  const priorityColor = { High: 'var(--red)', Medium: 'var(--gold)', Low: 'var(--green)' }
  const pending = tasks.filter(t => !t.done)
  const done = tasks.filter(t => t.done)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <StatCard label="Open Tasks" value={pending.length} accent="var(--gold)" />
        <StatCard label="High Priority" value={pending.filter(t => t.priority === 'High').length} accent="var(--red)" />
        <StatCard label="Completed" value={done.length} accent="var(--green)" />
      </div>

      <SectionLabel>Open Tasks</SectionLabel>
      <Card style={{ padding: 0 }}>
        {pending.map((t, i) => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderBottom: i < pending.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
            <button onClick={() => toggle(t.id)}
              style={{ width: 22, height: 22, borderRadius: 3, border: `2px solid ${priorityColor[t.priority]}`, background: 'transparent', cursor: 'pointer', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 14, color: 'var(--white)', marginBottom: 3 }}>{t.task}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{t.client}</div>
            </div>
            <Badge color={priorityColor[t.priority]}>{t.priority}</Badge>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700, letterSpacing: 2, color: t.due === 'Today' ? 'var(--red)' : 'var(--muted)', textTransform: 'uppercase' }}>{t.due}</div>
          </div>
        ))}
      </Card>

      {done.length > 0 && <>
        <SectionLabel>Completed</SectionLabel>
        <Card style={{ padding: 0 }}>
          {done.map((t, i) => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 18px', borderBottom: i < done.length - 1 ? '1px solid var(--border)' : 'none', opacity: 0.5 }}>
              <button onClick={() => toggle(t.id)}
                style={{ width: 22, height: 22, borderRadius: 3, border: '2px solid var(--green)', background: 'var(--green)', cursor: 'pointer', flexShrink: 0, color: 'white', fontSize: 12, fontWeight: 700 }}>✓</button>
              <div style={{ fontSize: 13, color: 'var(--muted)', textDecoration: 'line-through', flex: 1 }}>{t.task}</div>
            </div>
          ))}
        </Card>
      </>}
    </div>
  )
}

/* ── MARKETING ──────────────────────────────────────────────────────────── */
function AdminMarketing() {
  const outreach = [
    { name: 'Mac McClung', platform: 'Instagram', status: 'DM Sent', date: '2026-05-28', market: 'NBA/G-League', followers: '620K' },
    { name: 'Dylan Harper', platform: 'Instagram', status: 'No Response', date: '2026-05-27', market: 'NIL — Rutgers', followers: '85K' },
    { name: 'Bijan Robinson', platform: 'Instagram', status: 'Responded', date: '2026-05-26', market: 'NFL', followers: '420K' },
    { name: 'Ace Bailey', platform: 'Instagram', status: 'DM Sent', date: '2026-05-25', market: 'NIL — Rutgers', followers: '45K' },
    { name: 'Mozie Ingram', platform: 'Instagram', status: 'Call Booked', date: '2026-05-24', market: 'AAU Philly', followers: '12K' },
    { name: 'Tyler Smith', platform: 'Twitter/X', status: 'DM Sent', date: '2026-05-23', market: 'G-League', followers: '8K' },
  ]

  const statusColor = { 'Call Booked': 'var(--green)', 'Responded': '#1565C0', 'DM Sent': 'var(--gold)', 'No Response': 'var(--muted)' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <StatCard label="DMs Sent" value={outreach.length} sub="This week" accent="var(--gold)" />
        <StatCard label="Responses" value={outreach.filter(o => o.status !== 'No Response' && o.status !== 'DM Sent').length} accent="#1565C0" />
        <StatCard label="Calls Booked" value={outreach.filter(o => o.status === 'Call Booked').length} accent="var(--green)" />
        <StatCard label="Response Rate" value="67%" sub="Industry avg 20%" accent="var(--green)" />
      </div>

      <SectionLabel>Outreach Tracker</SectionLabel>
      <Card style={{ padding: 0 }}>
        {outreach.map((o, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderBottom: i < outreach.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)', marginBottom: 2 }}>{o.name}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{o.market} · {o.followers} followers · {o.platform}</div>
            </div>
            <Badge color={statusColor[o.status]}>{o.status}</Badge>
            <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, color: 'var(--muted)', letterSpacing: 1 }}>{o.date}</div>
          </div>
        ))}
      </Card>

      <SectionLabel>Instagram Performance</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <StatCard label="Followers" value="1.2K" sub="+840 this month" accent="var(--gold)" />
        <StatCard label="Posts" value="18" sub="This month" accent="var(--gold)" />
        <StatCard label="Engagement" value="4.2%" sub="Avg: 1.8%" accent="var(--green)" />
        <StatCard label="Top Post" value="12K" sub="Workout reel views" accent="#9C27B0" />
      </div>
    </div>
  )
}

/* ── CALENDAR ────────────────────────────────────────────────────────────── */
function AdminCalendar() {
  const events = [
    { date: 'Jun 1', day: 'Mon', title: 'Monthly Reports Due', client: 'All Clients', type: 'Deadline', color: 'var(--red)' },
    { date: 'Jun 2', day: 'Tue', title: 'Discovery Call — Shanice Davis', client: 'Pipeline', type: 'Call', color: 'var(--green)' },
    { date: 'Jun 3', day: 'Wed', title: 'Website Delivery — Jordan Rivera', client: 'Jordan Rivera', type: 'Deliverable', color: 'var(--gold)' },
    { date: 'Jun 5', day: 'Fri', title: 'Discovery Call — Marcus Lee', client: 'Pipeline', type: 'Call', color: 'var(--green)' },
    { date: 'Jun 5', day: 'Fri', title: 'Weekly Content Review', client: 'Marcus Thompson', type: 'Meeting', color: '#1565C0' },
    { date: 'Jun 8', day: 'Mon', title: 'Follow-up — DeShawn Morris', client: 'Pipeline', type: 'Outreach', color: '#9C27B0' },
    { date: 'Jun 10', day: 'Wed', title: 'Quarterly Review — Destiny Williams', client: 'Destiny Williams', type: 'Meeting', color: '#1565C0' },
    { date: 'Jun 15', day: 'Mon', title: 'Onboarding Call — Amir Johnson', client: 'Amir Johnson', type: 'Meeting', color: '#1565C0' },
    { date: 'Jun 20', day: 'Sat', title: 'Post 4 Instagram pieces', client: 'Marketing', type: 'Content', color: 'var(--gold)' },
  ]

  const typeColor = { Call: 'var(--green)', Deliverable: 'var(--gold)', Meeting: '#1565C0', Deadline: 'var(--red)', Outreach: '#9C27B0', Content: 'var(--gold)' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <StatCard label="This Week" value={events.filter(e => ['Jun 1','Jun 2','Jun 3','Jun 4','Jun 5'].includes(e.date)).length} sub="Events" accent="var(--gold)" />
        <StatCard label="Calls" value={events.filter(e => e.type === 'Call').length} sub="Booked" accent="var(--green)" />
        <StatCard label="Deliverables" value={events.filter(e => e.type === 'Deliverable').length} sub="Due this month" accent="var(--red)" />
      </div>

      <SectionLabel>June 2026</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {events.map((ev, i) => (
          <div key={i} style={{ display: 'flex', gap: 0, background: 'var(--card)', border: '1px solid var(--border)', overflow: 'hidden' }}>
            <div style={{ width: 72, background: ev.color + '22', borderRight: `3px solid ${ev.color}`, padding: '14px 10px', textAlign: 'center', flexShrink: 0 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, color: ev.color, lineHeight: 1 }}>{ev.date.split(' ')[1]}</div>
              <div style={{ fontFamily: 'var(--font-condensed)', fontSize: 9, fontWeight: 700, letterSpacing: 2, color: 'var(--muted)', textTransform: 'uppercase' }}>{ev.day}</div>
            </div>
            <div style={{ padding: '14px 18px', flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)', marginBottom: 4 }}>{ev.title}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Badge color={typeColor[ev.type]}>{ev.type}</Badge>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>{ev.client}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ── CONTENT MANAGER ─────────────────────────────────────────────────────── */
function AdminContent() {
  const [selected, setSelected] = useState('All')
  const clients = ['All', ...MOCK_CLIENTS.map(c => c.name.split(' ')[0])]
  const statusColor = { 'Approved': 'var(--green)', 'Pending': 'var(--gold)', 'Scheduled': '#1565C0' }

  const posts = [
    { client: 'Marcus', platform: 'Instagram', caption: 'The grind never stops. Another day another session. 🏀', status: 'Pending', date: '2026-06-02' },
    { client: 'Destiny', platform: 'TikTok', caption: 'Game day routine — 5am hits different when you got goals', status: 'Approved', date: '2026-06-01' },
    { client: 'Jordan', platform: 'Instagram', caption: 'Representing the US in Spain — living the dream 🇺🇸🇪🇸', status: 'Scheduled', date: '2026-06-03' },
    { client: 'Caleb', platform: 'Twitter/X', caption: 'NIL era is real. Build your brand while you play.', status: 'Pending', date: '2026-06-04' },
    { client: 'Amir', platform: 'Instagram', caption: 'First week in the league. Nothing but gratitude.', status: 'Approved', date: '2026-06-05' },
  ]

  const filtered = selected === 'All' ? posts : posts.filter(p => p.client === selected)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {clients.map(c => (
          <button key={c} onClick={() => setSelected(c)}
            style={{ background: selected === c ? 'var(--gold)' : 'var(--card)', color: selected === c ? 'var(--black)' : 'var(--light)', border: '1px solid var(--border)', fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', padding: '8px 16px', cursor: 'pointer' }}>
            {c}
          </button>
        ))}
      </div>
      <Card style={{ padding: 0 }}>
        {filtered.map((post, i) => (
          <div key={i} style={{ padding: '16px 20px', borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ background: 'var(--dark)', padding: '6px 12px', fontFamily: 'var(--font-condensed)', fontSize: 12, fontWeight: 700, letterSpacing: 2, color: 'var(--gold)', flexShrink: 0 }}>{post.client}</div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700, letterSpacing: 2, color: 'var(--muted)', textTransform: 'uppercase' }}>{post.platform}</span>
                <Badge color={statusColor[post.status]}>{post.status}</Badge>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>{post.date}</span>
              </div>
              <div style={{ fontSize: 14, color: 'var(--light)' }}>{post.caption}</div>
            </div>
          </div>
        ))}
      </Card>
    </div>
  )
}

/* ── INVITE CLIENT ───────────────────────────────────────────────────────── */
function AdminInvite() {
  const [form, setForm] = useState({ email: '', full_name: '', team: '', package_tier: 'Build', role: 'athlete' })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  async function handleInvite(e) {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')
    try {
      const tempPassword = Math.random().toString(36).slice(-10)
      const { data, error: signUpError } = await supabase.auth.admin.createUser({
        email: form.email, password: tempPassword, email_confirm: true,
      })
      if (signUpError) throw signUpError
      await supabase.from('profiles').upsert({
        id: data.user.id, email: form.email, full_name: form.full_name,
        team: form.team, package_tier: form.package_tier, role: form.role,
      })
      setSuccess(`Account created for ${form.full_name}. Temporary password: ${tempPassword} — send this to them securely.`)
      setForm({ email: '', full_name: '', team: '', package_tier: 'Build', role: 'athlete' })
    } catch (err) {
      setError(err.message || 'Something went wrong.')
    }
    setLoading(false)
  }

  return (
    <div style={{ maxWidth: 560 }}>
      <SectionLabel>Invite New Client</SectionLabel>
      <Card style={{ borderTop: '3px solid var(--gold)', padding: '36px 32px' }}>
        <form onSubmit={handleInvite} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {[
            { label: 'Full Name', key: 'full_name', type: 'text', placeholder: 'Marcus Thompson' },
            { label: 'Email Address', key: 'email', type: 'email', placeholder: 'marcus@email.com' },
            { label: 'Team / League', key: 'team', type: 'text', placeholder: 'G-League — Motor City Cruise' },
          ].map(field => (
            <div key={field.key}>
              <label style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 8 }}>{field.label}</label>
              <input type={field.type} value={form[field.key]} onChange={e => setForm(p => ({ ...p, [field.key]: e.target.value }))} placeholder={field.placeholder} required
                style={{ width: '100%', padding: '12px 16px', fontSize: 15 }} />
            </div>
          ))}
          <div>
            <label style={{ fontFamily: 'var(--font-condensed)', fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--muted)', display: 'block', marginBottom: 8 }}>Package</label>
            <select value={form.package_tier} onChange={e => setForm(p => ({ ...p, package_tier: e.target.value }))}
              style={{ width: '100%', padding: '12px 16px', fontSize: 15 }}>
              <option value="Launch">Launch — $1,500 one-time</option>
              <option value="Build">Build — $3,000/month</option>
              <option value="Operate">Operate — $7,500/month</option>
            </select>
          </div>
          {success && <div style={{ background: 'var(--green-d)', border: '1px solid var(--green)', padding: '14px 16px', fontSize: 14, color: 'var(--green)', lineHeight: 1.6 }}>{success}</div>}
          {error && <div style={{ background: 'var(--red-d)', border: '1px solid var(--red)', padding: '14px 16px', fontSize: 14, color: '#ff6b6b' }}>{error}</div>}
          <button type="submit" disabled={loading}
            style={{ background: loading ? 'var(--border)' : 'var(--gold)', color: 'var(--black)', border: 'none', padding: '16px', fontFamily: 'var(--font-condensed)', fontSize: 14, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase', marginTop: 4, cursor: 'pointer' }}>
            {loading ? 'Creating Account...' : 'Create Client Account →'}
          </button>
        </form>
      </Card>
    </div>
  )
}
